import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';
import { IOption } from '@cmps/ng-select';
import { TranslateService } from '@ngx-translate/core';

import { CoachableMetric, CoachingType } from '../../../coaching-base/general';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { DialogLayoutService } from '../dialog-layout.service';
import { TranslationKeyMap } from '../focus-selector.i18n';
import { FocusSelectorState } from '../focus-selector.state';
import { Column, Row, Sort, SortOrder } from '../grid/experimental.grid';
import { selectTemplate } from '../grid/reusable-grid.function';
import { GridSortClickEvent } from '../grid/reusable-grid.model';
import {
    ColumnID,
    MetricGrid,
} from '../model/focus-selector-metric-selection.grid.model';
import { FocusSelectorMetricModel } from '../model/focus-selector-metric.model';

import { getSelectedMetricFromGrid } from './metric-selection.function';

@Component({
    selector: 'cmps-metric-selection',
    templateUrl: './metric-selection.component.html',
    host: {
        class: 'cmps-metric-selection',
        '[class.row-mode]': '!_forceColumn',
    },
})
@TakeUntilDestroy()
export class MetricSelectionComponent implements OnInit, OnChanges {
    state: FocusSelectorState;

    @Input() metrices: FocusSelectorMetricModel[];
    @Input() gridData: MetricGrid;
    @Output() selectActiveMetric: EventEmitter<number>;
    @Output() selectMetricRow: EventEmitter<number>;
    @Output() sortMetricGrid: EventEmitter<GridSortClickEvent>;

    /** @private */ _i18n = TranslationKeyMap;
    /** @private */ _metrices: IOption[];
    /** @private */ _forceColumn: boolean;
    /** @private */ _SortOrder = SortOrder;
    /** @private */ _ColumnID = ColumnID;
    /** @private */ _CoachingType = CoachingType;
    /** @private */ _selectedMetric: FocusSelectorMetricModel;
    /** @private */ _selectTpl = selectTemplate;

    constructor(
        private readonly _dialogLayoutService: DialogLayoutService,
        private readonly _translateService: TranslateService,
    ) {
        this.selectActiveMetric = new EventEmitter();
        this.selectMetricRow = new EventEmitter();
        this.sortMetricGrid = new EventEmitter();
        this._metrices = [];
        this._forceColumn = false;
        this._selectedMetric = { id: null };
    }

    ngOnInit(): void {
        this._dialogLayoutService.forceColumn
            .pipe(takeUntilDestroy(this))
            .subscribe((forceColumn) => {
                this._forceColumn = forceColumn;
            });

        this._metrices = this.metrices.map((metric) => ({
            label: metric.name,
            value: metric.id,
        }));

        this._selectedMetric = getSelectedMetricFromGrid(this.gridData);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.gridData != null) {
            this._selectedMetric = getSelectedMetricFromGrid(this.gridData);
        }
    }

    /** @private */ _selectMetric($event: number): void {
        this.selectActiveMetric.emit($event);
    }

    /** @private */ _selectRow(selectedRows: Set<Row['id']>): void {
        const selected = Array.from(selectedRows)[0];
        this.selectMetricRow.emit(parseInt(selected));
    }

    /** @private */ _getCoachableTranslationText(
        coachTypeId: CoachableMetric['coachTypeId'],
    ): string {
        switch (coachTypeId) {
            case this._CoachingType.NON_OPPORTUNITY:
                return this._translateService.instant(
                    TranslationKeyMap._11090_METRIC_IS_ALREADY_BEING_COACHED_BUT_IS_NOT_AN_OPPORTUNITY_KPI,
                );
            case this._CoachingType.COACHED:
                return this._translateService.instant(
                    TranslationKeyMap._11089_METRIC_IS_ALREADY_BEING_COACHED,
                );
            case this._CoachingType.UNCOACHED:
                return this._translateService.instant(
                    TranslationKeyMap._11088_METRIC_IS_AN_CURRENT_OPPORTUNITY,
                );
            default:
                return '';
        }
    }

    /** @private */ _getSortDef(columnId: Column['id']): Sort {
        for (const sortDef of this.gridData.sortDefs) {
            if (sortDef.columnId === columnId) {
                return sortDef;
            }
        }

        console.warn('No sortDef found for column: ', columnId);
    }
}
